import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import {
  erFarge, FARGER, fraNoder, medHuskepunkt, sorterHuskepunkt, tilNoder,
  type Farge, type Tekstdok,
} from "@infoflyt/shared/tekst";

/** Navnene i nedtrekket. «Ingen farge» staar oeverst og er standarden. */
const FARGENAVN: Readonly<Record<Farge, string>> = {
  rod: "Rød",
  gul: "Gul",
  gronn: "Grønn",
  bla: "Blå",
};

/**
 * Skrivefeltet med verktøylinje.
 *
 * KNAPPENE VISER HVA DE GJØR. Kursivknappen står i kursiv, understrek er
 * understreket, gjennomstreking er strøket over. Det er billigere å lese enn
 * et ikon man må lære, og det er riktig her: dette er en flate folk bruker
 * noen minutter om dagen, ikke et verktøy de blir eksperter i.
 *
 * `document.execCommand` er merket utdatert, men den gjør fortsatt jobben i
 * alle nettlesere som finnes — og alternativet er å skrive en hel
 * tekstmotor. Det er trygt her av én grunn: INNHOLDET LESES TILBAKE SOM
 * NODER, ikke som HTML. Hva kommandoen enn produserer, er det bare det
 * modellen kjenner igjen som overlever. Se `@infoflyt/shared/tekst`.
 *
 * Fargen er unntaket: `foreColor` lager `<font color>`, som modellen ikke
 * kjenner. Den settes derfor med Range-API-et, og elementet lages av oss.
 */

/**
 * Markøren til slutten av en node.
 *
 * En tom linje har bare en `<br>`. Markøren skal stå FORAN den — bak den
 * blir det en linje til.
 */
function settMarkoerSist(node: Node): void {
  const r = document.createRange();
  const el = node.nodeType === 1 ? (node as Element) : null;
  if (el !== null && el.childNodes.length === 1 && el.firstChild?.nodeName === "BR") {
    r.setStart(el, 0);
    r.collapse(true);
  } else {
    r.selectNodeContents(node);
    r.collapse(false);
  }
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(r);
}

/** Hvor mange tegn det er fra blokkas start fram til markøren. */
function tegnFoer(blokk: Element, node: Node, off: number): number {
  const r = document.createRange();
  r.selectNodeContents(blokk);
  try {
    r.setEnd(node, off);
  } catch {
    return 0;
  }
  return r.toString().length;
}

/**
 * Setter markøren `n` tegn inn i blokka.
 *
 * Brukes når en blokk byttes ut med en av et annet slag: man skal bli
 * stående der man stod, ikke kastes til enden av linja.
 */
function settMarkoerVed(blokk: Element, n: number): void {
  const gaa = document.createTreeWalker(blokk, NodeFilter.SHOW_TEXT);
  let igjen = n;
  let t = gaa.nextNode() as Text | null;
  while (t !== null) {
    if (igjen <= t.length) {
      const r = document.createRange();
      r.setStart(t, igjen);
      r.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
      return;
    }
    igjen -= t.length;
    t = gaa.nextNode() as Text | null;
  }
  settMarkoerSist(blokk);
}

interface Verktoy {
  readonly id: string;
  readonly merke: string;
  readonly tittel: string;
  readonly klasse?: string;
  kjor(): void;
}

export function Rikfelt({
  dok, onEndret, onFerdig, plassholder, listemodus = false, skrivbar = true,
}: {
  readonly dok: Tekstdok;
  readonly plassholder: string;
  /**
   * Av «Huskeliste»-overskriften i feltet.
   *
   * I shotlista er hele dokumentet lista, og en overskrift over det som
   * uansett er alt sier ingenting.
   */
  readonly listemodus?: boolean;
  /**
   * Av naar feltet bare skal LESES.
   *
   * Briefen krever spesialtillatelse fra 02.11.2026 — se
   * `tilgang.skriver_tekstflate`. Alle leser alle tre feltene, men bare
   * den som har retten skriver i sitt. Et felt man kan skrive i og som
   * basen saa avviser er verre enn et felt som staar stille.
   */
  readonly skrivbar?: boolean;
  onEndret(dok: Tekstdok): void;
  onFerdig(dok: Tekstdok): void;
}): JSX.Element {
  const felt = useRef<HTMLDivElement | null>(null);
  /*
   * Dokumentet slik det står i feltet NÅ.
   *
   * Feltet er ukontrollert: skrev vi innholdet inn på nytt ved hver endring,
   * ville markøren hoppet til starten for hvert tegn. Derfor settes nodene
   * bare når dokumentet kommer utenfra, og `sist` holder styr på hva som
   * allerede står der.
   */
  const sist = useRef<string>("");
  const [fargeAapen, setFargeAapen] = useState(false);
  /* Fargen som gjelder DER MARKØREN STÅR. Knappen viser den. */
  const [fargeNa, setFargeNa] = useState<Farge | null>(null);

  useEffect(() => {
    const el = felt.current;
    if (el === null) return;
    const neste = JSON.stringify(dok);
    if (neste === sist.current) return;
    sist.current = neste;
    el.replaceChildren(tilNoder(dok, document));
  }, [dok]);

  /*
   * Knappen viser fargen markøren står i.
   *
   * `selectionchange` og ikke bare et svar på valget: fargen skal stemme
   * også når man klikker seg inn i noe som alt er farget, eller blar seg dit
   * med piltastene. En knapp som bare husker det siste man valgte, lyver så
   * fort man flytter seg.
   */
  useEffect(() => {
    const se = (): void => {
      const el = felt.current;
      const utvalg = window.getSelection();
      if (el === null || utvalg === null || utvalg.rangeCount === 0) return;
      const n = utvalg.getRangeAt(0).startContainer;
      if (!el.contains(n)) return;
      const e = n.nodeType === 1 ? (n as Element) : n.parentElement;
      const merket = e?.closest("[data-farge]") ?? null;
      const f = merket?.getAttribute("data-farge") ?? null;
      setFargeNa(f !== null && erFarge(f) ? f : null);
    };
    document.addEventListener("selectionchange", se);
    return () => { document.removeEventListener("selectionchange", se); };
  }, []);

  /*
   * Et trykk utenfor lukker nedtrekket.
   *
   * HER LAA FEILEN som gjorde at menyen ikke kom opp i det hele tatt.
   * Lytteren settes opp naar menyen aapnes — altsaa MIDT I det trykket som
   * aapnet den — og siden hendelsen fortsatt var paa vei opp mot `window`,
   * tok den nye lytteren imot sitt eget trykk og lukket menyen igjen i
   * samme oeyeblikk. Knappene stopper derfor hendelsen sin naa, saa den
   * aldri naar hit.
   */
  useEffect(() => {
    if (!fargeAapen) return;
    const ut = (): void => { setFargeAapen(false); };
    window.addEventListener("mousedown", ut);
    return () => { window.removeEventListener("mousedown", ut); };
  }, [fargeAapen]);

  const les = useCallback((): Tekstdok => {
    const el = felt.current;
    if (el === null) return dok;
    const d = fraNoder(el);
    sist.current = JSON.stringify(d);
    return d;
  }, [dok]);

  const kommando = useCallback((navn: string, verdi?: string): void => {
    felt.current?.focus();
    document.execCommand(navn, false, verdi);
    onEndret(les());
  }, [les, onEndret]);

  /** Blokka markøren står i, eller null. */
  const blokkNa = useCallback((): Element | null => {
    const el = felt.current;
    const utvalg = window.getSelection();
    if (el === null || utvalg === null || utvalg.rangeCount === 0) return null;
    const n = utvalg.getRangeAt(0).startContainer;
    const e = n.nodeType === 1 ? (n as Element) : n.parentElement;
    const blokk = e?.closest("h2,h3,p,li") ?? null;
    return blokk !== null && el.contains(blokk) ? blokk : null;
  }, []);

  /**
   * Overskrift, underoverskrift og vanlig tekst.
   *
   * BRYTERE: står man alt i en h2 og trykker «Overskrift», blir det et
   * avsnitt igjen. Kursiv og de andre merkene har alltid oppført seg slik —
   * en knapp som bare går én vei er en knapp man ikke tør trykke på.
   *
   * GJØRES HER, IKKE MED `formatBlock`. To grunner, og begge er meldt som
   * feil: kommandoen gjør ingenting i det hele tatt på en listelinje, så
   * «Vanlig tekst» var død på et kulepunkt og på et huskepunkt — og
   * nettleserne er uenige om hva den gjør ellers, så bryteren virket i én
   * og ikke i en annen. Vi bytter ut elementet selv. Da er svaret det samme
   * overalt, og en listelinje kan gå ut av lista slik den skal.
   */
  const settBlokktype = useCallback((tag: "h2" | "h3" | "p"): void => {
    const el = felt.current;
    const na = blokkNa();
    if (el === null || na === null) return;
    const er = na.tagName.toLowerCase();
    const maal = er === tag ? "p" : tag;
    /* Et avsnitt som skal bli et avsnitt er ingen endring. En listelinje
       som skal bli et avsnitt er det, selv om begge «er» p. */
    if (er === maal && na.tagName !== "LI") return;

    const utvalg = window.getSelection();
    const plass = utvalg === null || utvalg.rangeCount === 0
      ? 0
      : tegnFoer(na, utvalg.getRangeAt(0).startContainer, utvalg.getRangeAt(0).startOffset);

    const ny = document.createElement(maal);
    while (na.firstChild !== null) ny.appendChild(na.firstChild);

    if (er === "li") {
      /*
       * Linja skal UT av lista, og lista kan bli delt i to: det som lå
       * under flyttes til sin egen liste under den nye blokka. Uten det
       * ville resten av punktene havnet over linja man nettopp gjorde om.
       */
      const liste = na.parentElement;
      const under = liste === null ? [] : [...liste.children].slice(
        [...liste.children].indexOf(na) + 1,
      );
      liste?.after(ny);
      if (under.length > 0) {
        const rest = document.createElement("ul");
        rest.append(...under);
        ny.after(rest);
      }
      na.remove();
      if (liste !== null && liste.children.length === 0) liste.remove();
    } else {
      na.replaceWith(ny);
    }

    el.focus();
    settMarkoerVed(ny, plass);
    onEndret(les());
  }, [blokkNa, les, onEndret]);

  /**
   * Backspace på et TOMT huskepunkt fjerner punktet.
   *
   * Nettleserne er uenige om hva tasten gjør her. I én forsvinner linja; i
   * en annen flytter bare markøren seg opp av lista mens punktet og
   * overskriften blir stående — det ser ut som om noe ble slettet, men
   * ingenting ble det, og man må ned igjen og trykke en gang til.
   *
   * Huskelista er vår, så tasten er det også. Kulepunktene lar vi
   * nettleseren styre: der er «ut av lista» en innarbeidet oppførsel.
   */
  const kanskjeSlettPunkt = useCallback((e: React.KeyboardEvent): void => {
    if (e.key !== "Backspace") return;
    const el = felt.current;
    const utvalg = window.getSelection();
    if (el === null || utvalg === null || !utvalg.isCollapsed) return;
    const na = blokkNa();
    if (na === null || na.tagName !== "LI" || !na.hasAttribute("data-huske")) return;
    if ((na.textContent ?? "").replace(/\u200B/g, "").length > 0) return;

    e.preventDefault();
    const liste = na.parentElement;
    const foer = na.previousElementSibling ?? liste?.previousElementSibling ?? null;
    na.remove();
    if (liste !== null && liste.children.length === 0) liste.remove();
    el.focus();
    if (foer !== null) settMarkoerSist(foer);
    onEndret(les());
  }, [blokkNa, les, onEndret]);

  /**
   * Legger en linje i huskelista.
   *
   * IKKE en bryter på linja man står i. Her gjorde den forrige utgaven feil:
   * den gjorde linja man stod på om til et huskepunkt, og da forsvant
   * teksten ut av brødteksten. En beskjed og et huskepunkt er to ting —
   * «dette må du vite» og «dette må du gjøre» — og den ene skal ikke bli den
   * andre ved et uhell.
   *
   * Er noe markert, KOPIERES det ned i lista; originalen blir stående. Er
   * ingenting markert, lages en tom linje nederst og markøren settes der.
   */
  const tilHuskelista = useCallback((): void => {
    const el = felt.current;
    if (el === null) return;
    const utvalg = window.getSelection();
    const valgt = utvalg === null ? "" : utvalg.toString().trim();
    const neste = medHuskepunkt(sorterHuskepunkt(les()), valgt);

    /*
     * Nodene skrives om HER, og markøren settes i samme åndedrag.
     *
     * Det stod en `setTimeout` her, fordi effekten som tegner nodene først
     * kjører når det nye dokumentet kommer tilbake som egenskap. Den
     * ventingen var feilen: rakk man å taste før timeren gikk, havnet
     * teksten der markøren stod fra før — gjerne i overskriften øverst —
     * og punktet man nettopp lagde ble stående tomt.
     *
     * `sist` settes før nodene skrives, så effekten ser at feltet alt viser
     * det den skulle tegnet, og lar markøren være.
     */
    sist.current = JSON.stringify(neste);
    el.replaceChildren(tilNoder(neste, document));

    const rader = el.querySelectorAll("li[data-huske]");
    const rad = rader[rader.length - 1];
    if (rad !== undefined) {
      el.focus();
      settMarkoerSist(rad);
    }
    onEndret(neste);
  }, [les, onEndret]);

  /**
   * Et klikk i venstremargen på et huskepunkt huker det av.
   *
   * Boksen er tegnet med `::before` og finnes ikke som node — man kan ikke
   * lytte på den. I stedet regnes det ut om trykket traff margen der boksen
   * står, og hele margen er trykkflaten. Det er en større og roligere flate
   * enn elleve piksler, og teksten begynner uansett etter den.
   *
   * `preventDefault` holder markøren der den var: man huker av, man flytter
   * ikke skrivepunktet.
   */
  const kanskjeHuke = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    const maal = e.target as Element | null;
    const li = maal?.closest?.("li[data-huske]") ?? null;
    if (li === null) return;
    const r = li.getBoundingClientRect();
    const marg = Number.parseFloat(getComputedStyle(li).paddingLeft) || 22;
    if (e.clientX - r.left > marg) return;
    e.preventDefault();
    li.setAttribute("data-huske", li.getAttribute("data-huske") === "1" ? "0" : "1");
    onEndret(les());
  }, [les, onEndret]);

  /**
   * Fargen settes med Range-API-et, ikke med `foreColor`.
   *
   * Gamle farger i utvalget fjernes først. Uten det ville en ny farge lagt
   * seg utenpå den gamle, og den innerste — den usynlige — ville vunnet når
   * modellen leser nodene.
   */
  const settFarge = useCallback((farge: Farge | null): void => {
    const el = felt.current;
    const utvalg = window.getSelection();
    if (el === null || utvalg === null || utvalg.rangeCount === 0) return;
    const omraade = utvalg.getRangeAt(0);
    if (!el.contains(omraade.commonAncestorContainer)) return;
    el.focus();

    /*
     * INGEN MARKERING: fargen gjelder det som skal SKRIVES.
     *
     * Her manglet halve funksjonen. Man velger farge og skriver — slik en
     * penn virker — men uten markering fantes det ingenting å farge, og
     * valget falt på gulvet.
     *
     * Et tomt element holder ikke på markøren, så det legges et usynlig
     * tegn i det. Tegnet vaskes bort når dokumentet leses, og finnes derfor
     * aldri i noe som lagres. Se `@infoflyt/shared/tekst`.
     */
    if (omraade.collapsed) {
      const start = omraade.startContainer;
      const node = start.nodeType === 1 ? (start as Element) : start.parentElement;

      if (farge === null) {
        // Av: et holdepunkt UTENFOR den fargede boksen, så videre skriving
        // havner utenfor den.
        const inne = node?.closest("[data-farge]") ?? null;
        if (inne === null) return;
        const etter = document.createTextNode("\u200B");
        inne.after(etter);
        settMarkoerSist(etter);
        return;
      }

      const ny = document.createElement("span");
      ny.setAttribute("data-farge", farge);
      ny.appendChild(document.createTextNode("\u200B"));
      omraade.insertNode(ny);
      const inni = ny.firstChild;
      if (inni !== null) settMarkoerSist(inni);
      return;
    }

    for (const gammel of Array.from(el.querySelectorAll("[data-farge]"))) {
      if (omraade.intersectsNode(gammel)) gammel.removeAttribute("data-farge");
    }
    if (farge !== null) {
      const span = document.createElement("span");
      span.setAttribute("data-farge", farge);
      span.appendChild(omraade.extractContents());
      omraade.insertNode(span);
      utvalg.removeAllRanges();
      utvalg.selectAllChildren(span);
    }
    setFargeNa(farge);
    onEndret(les());
  }, [les, onEndret]);

  const verktoy: readonly Verktoy[] = [
    {
      id: "h2", merke: "H", tittel: "Overskrift", klasse: "rikfelt__h1",
      kjor: () => settBlokktype("h2"),
    },
    {
      id: "h3", merke: "H", tittel: "Underoverskrift", klasse: "rikfelt__h2",
      kjor: () => settBlokktype("h3"),
    },
    {
      id: "p", merke: "¶", tittel: "Vanlig tekst",
      kjor: () => settBlokktype("p"),
    },
    {
      id: "fet", merke: "F", tittel: "Fet", klasse: "rikfelt__fet",
      kjor: () => kommando("bold"),
    },
    {
      id: "kursiv", merke: "K", tittel: "Kursiv", klasse: "rikfelt__kursiv",
      kjor: () => kommando("italic"),
    },
    {
      id: "understrek", merke: "U", tittel: "Understrek", klasse: "rikfelt__understrek",
      kjor: () => kommando("underline"),
    },
    {
      id: "strek", merke: "S", tittel: "Strek over", klasse: "rikfelt__strek",
      kjor: () => kommando("strikeThrough"),
    },
    {
      id: "punkt", merke: "•", tittel: "Punktliste",
      kjor: () => kommando("insertUnorderedList"),
    },
  ];

  return (
    <div className={`rikfelt${listemodus ? " rikfelt--liste" : ""}`}>
      <div className="rikfelt__verktoy" role="toolbar" aria-label="Formatering">
        {verktoy.map((v, i) => (
          <span key={v.id} style={{ display: "contents" }}>
            {/* Skillene grupperer: blokk, merke, liste. Tre grupper er
                lettere å finne fram i enn sju knapper på rad. */}
            {i === 3 || i === 7 ? <span className="rikfelt__skille" aria-hidden="true" /> : null}
            <button
              type="button"
              className={`rikfelt__knapp${v.klasse === undefined ? "" : ` ${v.klasse}`}`}
              title={v.tittel}
              aria-label={v.tittel}
              // `mousedown` og ikke `click`: et klikk flytter fokus ut av
              // feltet og tar utvalget med seg før kommandoen rekker å gå.
              onMouseDown={(e) => { e.preventDefault(); v.kjor(); }}
            >
              {v.merke}
            </button>
          </span>
        ))}

        <span className="rikfelt__skille" aria-hidden="true" />
        {/*
          Huskepunktet staar for seg, med tekst paa.
          Det er den ene knappen her som ikke bare former tekst — den lager
          noe folk skal HUKE AV i appen, og den skal finnes uten at man
          leter.
        */}
        <button
          type="button"
          className="rikfelt__huske"
          title="Huskepunkt — kan hukes av i appen"
          aria-label="Huskepunkt"
          onMouseDown={(e) => { e.preventDefault(); tilHuskelista(); }}
        >
          <span className="rikfelt__hukeboks" aria-hidden="true" />
          Huskepunkt
        </button>

        <span className="rikfelt__skille" aria-hidden="true" />
        {/*
          Nedtrekk og ikke fem prikker paa rad.
          Fargen brukes sjelden, og fem like sirkler tok like mye plass som
          alt det andre til sammen. `mousedown` med `preventDefault` overalt:
          et klikk ville tatt fokus ut av feltet og utvalget med seg.
        */}
        <span className="rikfelt__fargevalg">
          <button
            type="button"
            className="rikfelt__fargeknapp"
            aria-haspopup="menu"
            aria-expanded={fargeAapen}
            title={fargeNa === null ? "Farge på teksten" : `Farge: ${FARGENAVN[fargeNa]}`}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setFargeAapen((v) => !v);
            }}
          >
            <span
              className={`rikfelt__farge${fargeNa === null ? " rikfelt__farge--ingen" : ""}`}
              style={fargeNa === null ? undefined : { background: `var(--tekst-${fargeNa})` }}
              aria-hidden="true"
            />
            <span className="rikfelt__pil" aria-hidden="true">▾</span>
          </button>

          {fargeAapen ? (
            <span className="rikfelt__fargeliste" role="menu">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={fargeNa === null}
                className={`rikfelt__fargerad${fargeNa === null ? " rikfelt__fargerad--na" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  settFarge(null);
                  setFargeAapen(false);
                }}
              >
                <span className="rikfelt__farge rikfelt__farge--ingen" aria-hidden="true" />
                Ingen farge
              </button>
              {FARGER.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="menuitemradio"
                  aria-checked={fargeNa === f}
                  className={`rikfelt__fargerad${fargeNa === f ? " rikfelt__fargerad--na" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    settFarge(f);
                    setFargeAapen(false);
                  }}
                >
                  <span
                    className="rikfelt__farge"
                    style={{ background: `var(--tekst-${f})` }}
                    aria-hidden="true"
                  />
                  {FARGENAVN[f]}
                </button>
              ))}
            </span>
          ) : null}
        </span>
      </div>

      <div
        ref={felt}
        className="rikfelt__flate"
        contentEditable={skrivbar}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={plassholder}
        /* Teksten arves ned til den tomme blokka, som tegner den. Se CSS-en. */
        style={{ "--plassholder": JSON.stringify(plassholder) } as CSSProperties}
        onMouseDown={kanskjeHuke}
        onKeyDown={kanskjeSlettPunkt}
        onInput={() => onEndret(les())}
        /* Huskepunktene samles nederst naar man forlater feltet — ikke mens
           man skriver, da ville linja hoppet under fingeren. */
        onBlur={() => onFerdig(sorterHuskepunkt(les()))}
      />
    </div>
  );
}
